import type {
  BackupStatus,
  BpmnLayout,
  DeployResult,
  DeployToManyResult,
  FlowDetail,
  FlowSummary,
  RuntimeStatus,
  FlowParams,
  TestRunRequest,
  TestRunResponse,
  RunHistory,
  ValueMappingSummary,
  ValueMappingDoc,
  MessageMappingSummary,
  MessageMappingDoc,
  RuntimeSummary,
  RuntimeRecord,
  RuntimeEnvironment,
  CreateRuntimeBody,
  UpdateRuntimeBody,
  ProvisionRuntimeBody,
  RuntimeContainerState,
  RuntimeDriftStatus,
  DriftSummary,
  RepairDriftResult,
  FullSyncResult,
  PipeConnectionEvent,
  RuntimeHealth,
  ParameterSummary,
  CreateParameterBody,
  UpdateParameterBody,
  ResolvedParameters,
  Role,
  Policy,
  PolicyInput,
  PolicyCheckResult,
  CreateRoleBody,
  UpdateRoleBody,
  OAuthClientSummary,
  CreateOAuthClientBody,
  UpdateOAuthClientBody,
  CreateOAuthClientResponse,
  MessageListResponse,
  MessageQuery,
  MessageStatsResponse,
  ConnectionRecord,
  ConnectionInputBody,
  ConnectionHealth,
  PulsarCluster,
  PulsarTopicSummary,
  PulsarTopicStats,
  PulsarSubscription,
  PulsarSchemaEntry,
  PulsarSchemaVersion,
  PulsarTenantSummary,
  PulsarTenantDetail,
  PulsarTenantAuditEntry,
  CreatePulsarTenantBody,
  UpdatePulsarTenantBody,
  NamespaceQuotaInfo,
  CreateTopicBody,
  WorkspaceSummary,
  CreateWorkspaceBody,
  UpdateWorkspaceBody,
  WorkspaceMembership,
  WorkspaceRole,
  WorkspaceBranding,
  GitopsConfig,
  GitopsConflictRow,
  AuthMeResponse,
  SsoConfigResponse,
  SsoConfigInput,
  FlowDeploymentsResponse,
  PromoteFlowResponse,
  EventDesignSummary,
  EventDesignDocument,
  CreateEventDesignBody,
  PutEventDesignBody,
  ApplyEventDesignResponse,
  EventDesignTopicListItem,
  TopicOwnersMap,
  TopicCatalogEntry,
  DiscoverFromBrokerBody,
  DiscoverFromBrokerResponse,
  FlowSlo,
  ObservabilityConfig,
  OnboardingState,
  TlsHealthResponse,
  AuditLogPage,
  AuditLogQuery,
  DeploymentsMatrix,
  CutoverView,
  CutoverHistoryEntry,
  QueueListResponse,
  QueueBrowseResponse,
  QueuePurgeResponse,
  PoolSummary,
  CreatePoolBody,
  UpdatePoolBody,
  PoolDeployBody,
  PoolDeployResult,
  DuplicateFiringWarning,
} from './types';
import type { SubFlowContract } from './next/subflowContract';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Pull the most descriptive message we can from a backend error body.
 *  Backend routes return `{error: "..."}` (sometimes with `issues: [...]`
 *  from zod). The legacy contract was `{message: "..."}` so we accept
 *  either. We also fall back to `issues[].message` so zod parse errors
 *  surface their first complaint instead of "Request failed: 400". */
function extractErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    // A deploy refused by a policy (spec 2026-11-02): name the rules, so
    // every deploy button says why without its own handling.
    if (Array.isArray(o.violations) && o.violations.length > 0) {
      const vs = o.violations as Array<{ policyName?: unknown; message?: unknown }>;
      const parts = vs.slice(0, 3).map((v) => `${String(v.policyName ?? 'Policy')}: ${String(v.message ?? '')}`);
      return `Blocked by policy. ${parts.join('; ')}${vs.length > 3 ? ` (+${vs.length - 3} more)` : ''}`;
    }
    if (typeof o.error === 'string' && o.error.trim().length > 0) return o.error;
    if (typeof o.message === 'string' && o.message.trim().length > 0) return o.message;
    // zod issues — take the first one's message + path so the user knows
    // which field is wrong.
    if (Array.isArray(o.issues) && o.issues.length > 0) {
      const first = o.issues[0] as { message?: unknown; path?: unknown };
      if (typeof first.message === 'string') {
        const path = Array.isArray(first.path) ? first.path.join('.') : '';
        return path ? `${path}: ${first.message}` : first.message;
      }
    }
  }
  // Friendly fallbacks for the gateway-class statuses — these almost
  // always come from Coolify/Traefik when the designer container is
  // restarting after a deploy. Telling the user "request failed: 503"
  // is unhelpful; "designer is restarting" tells them to retry.
  switch (status) {
    case 502:
    case 503:
    case 504:
      return 'Designer is restarting (gateway returned ' + status + '). Try again in a moment.';
    case 401:
      return 'Not authorised (401)';
    case 404:
      return 'Not found (404)';
    case 408:
      return 'Request timed out (408)';
    case 409:
      return 'Another user edited this flow. Reload to see their changes before saving again.';
    default:
      return `Request failed: ${status}`;
  }
}

/**
 * Active workspace slug — set by ActiveWorkspaceContext on mount + when
 * the user picks a different workspace from the topbar switcher. The
 * `request()` helper auto-injects this onto every outgoing API call as
 * `?workspace=<slug>` (or, when a query string already exists, appended
 * with `&`). Set to null/empty to fall back to backend default-workspace
 * resolution. */
let activeWorkspaceSlug: string | null = null;
export function setActiveWorkspaceSlug(slug: string | null): void {
  activeWorkspaceSlug = typeof slug === 'string' && slug.length > 0 ? slug : null;
}
export function getActiveWorkspaceSlug(): string | null {
  return activeWorkspaceSlug;
}

function withWorkspaceQuery(path: string): string {
  if (!activeWorkspaceSlug) return path;
  // Don't double-stamp if the caller already supplied workspace=...
  if (/[?&]workspace=/.test(path)) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}workspace=${encodeURIComponent(activeWorkspaceSlug)}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  // FormData uploads must let the browser set the multipart Content-Type
  // (with boundary). If we hard-set application/json, the multipart parser
  // on the backend explodes.
  const isMultipart = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  try {
    res = await fetch(withWorkspaceQuery(path), {
      ...init,
      headers: isMultipart
        ? { ...(init?.headers ?? {}) }
        : {
            'Content-Type': 'application/json',
            ...(init?.headers ?? {}),
          },
    });
  } catch (e) {
    // Network failure — the fetch itself rejected. Distinguish from a
    // backend-returned error so the toast can carry an actionable message.
    const msg = e instanceof Error ? e.message : 'network error';
    throw new ApiError(0, `Network error: ${msg}`);
  }
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(res.status, extractErrorMessage(body, res.status), body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  listFlows(): Promise<FlowSummary[]> {
    return request<FlowSummary[]>('/api/flows');
  },

  getFlow(id: string): Promise<FlowDetail> {
    return request<FlowDetail>(`/api/flows/${encodeURIComponent(id)}`);
  },

  /**
   * Save a flow. When `expectedEtag` is provided, the request includes an
   * `If-Match` header so the backend can reject stale writes. A 409 surfaces
   * as `ApiError` with `status === 409` — the editor handles it by toasting
   * "Another user edited this flow" and prompting a reload (US-005).
   *
   * `subflow` (SF-8) marks this flow as a reusable sub-flow:
   *   - a contract object → backend writes `subflow.json` + sets the flag
   *   - `null` → backend clears the sub-flow status
   *   - omitted (`undefined`) → backend leaves sub-flow status untouched
   * It's only included in the PUT body when the caller passes the key, so
   * existing callers (which omit it) are unaffected.
   */
  saveFlow(
    id: string,
    body: { yaml: string; layout: BpmnLayout; name?: string; subflow?: SubFlowContract | null },
    expectedEtag?: number,
  ): Promise<FlowDetail> {
    const headers: Record<string, string> =
      expectedEtag !== undefined ? { 'If-Match': String(expectedEtag) } : {};
    // Only serialize `subflow` when the caller explicitly supplied the key
    // (including an explicit `null` to clear). Omitting it leaves the backend's
    // existing sub-flow status untouched.
    const payload: Record<string, unknown> = { yaml: body.yaml, layout: body.layout };
    if (body.name !== undefined) payload.name = body.name;
    if ('subflow' in body) payload.subflow = body.subflow;
    return request<FlowDetail>(`/api/flows/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers,
    });
  },

  createFlow(body: { name?: string }): Promise<FlowDetail> {
    return request<FlowDetail>(`/api/flows`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  },

  deleteFlow(id: string): Promise<void> {
    return request<void>(`/api/flows/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  deploy(id: string, runtimeId?: string): Promise<DeployResult> {
    const base = `/api/flows/${encodeURIComponent(id)}/deploy`;
    return request<DeployResult>(runtimeId ? `${base}?runtimeId=${encodeURIComponent(runtimeId)}` : base, {
      method: 'POST',
    });
  },

  /** Undeploy: stop + remove the route from the runtime, but keep the
   *  flow's design on disk so the user can redeploy later. */
  undeploy(id: string, runtimeId?: string): Promise<{ ok: boolean; runtimeId?: string; alreadyAbsent?: boolean; error?: string }> {
    const q = runtimeId ? `?runtimeId=${encodeURIComponent(runtimeId)}` : '';
    return request(`/api/flows/${encodeURIComponent(id)}/undeploy${q}`, {
      method: 'POST',
    });
  },

  /** Bulk operation across N flows. The backend dispatches each id through
   *  the existing per-id handler so all auth / runtime / parameter logic
   *  stays consistent. Returns per-id results for the SPA to render. */
  bulkFlowAction(
    body: {
      ids: string[];
      action: 'deploy' | 'undeploy' | 'delete';
      options?: { runtimeId?: string };
    },
  ): Promise<{
    results: Array<{
      id: string;
      ok: boolean;
      error?: string;
      deployedAt?: string;
      status?: number;
    }>;
  }> {
    return request('/api/flows/bulk', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  runtimeStatus(): Promise<RuntimeStatus> {
    return request<RuntimeStatus>('/api/runtime/status');
  },

  /** Read backup-cron health from the designer backend. Returns
   *  `{ configured: false }` when the operator hasn't bind-mounted the
   *  backup snapshots dir into the designer container. See PRD
   *  backup-dr US-005 + coolify/BACKUP-DR.md for setup. */
  backupStatus(): Promise<BackupStatus> {
    return request<BackupStatus>('/api/backup/status');
  },

  flowParams(id: string): Promise<FlowParams> {
    return request<FlowParams>(`/api/flows/${encodeURIComponent(id)}/params`);
  },

  saveFlowParams(id: string, params: Record<string, string>): Promise<FlowParams> {
    return request<FlowParams>(`/api/flows/${encodeURIComponent(id)}/params`, {
      method: 'PUT',
      body: JSON.stringify({ params }),
    });
  },

  testFlow(id: string, body: TestRunRequest, runtimeId?: string): Promise<TestRunResponse> {
    const base = `/api/flows/${encodeURIComponent(id)}/test`;
    return request<TestRunResponse>(runtimeId ? `${base}?runtimeId=${encodeURIComponent(runtimeId)}` : base, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  flowRuns(id: string, limit = 50): Promise<RunHistory> {
    return request<RunHistory>(
      `/api/flows/${encodeURIComponent(id)}/runs?limit=${limit}`
    );
  },

  // ─── Live trace capture session (DX live-data-on-canvas) ────────────
  // A trace session arms a runtime to emit per-step capture events on the
  // SSE stream below. Always start the session BEFORE triggering the run,
  // and always stop it on completion (idempotently). Pass `runtimeId` to
  // target a specific runtime; omit to target the default runtime.

  /** Arm a per-step capture session on the runtime. Returns the
   *  session id + its TTL so the SSE consumer can filter to `sessionId`.
   *  When `runtimeId` is omitted, targets the default runtime. */
  traceSessionStart(flowId: string, runtimeId?: string): Promise<{ sessionId: string; ttlMs: number }> {
    const base = `/api/flows/${encodeURIComponent(flowId)}/trace/session`;
    return request(runtimeId ? `${base}?runtimeId=${encodeURIComponent(runtimeId)}` : base, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /** Tear down a capture session (204). Safe to call even if the session
   *  already expired server-side. When `runtimeId` is omitted, targets
   *  the default runtime. */
  traceSessionStop(flowId: string, sessionId: string, runtimeId?: string): Promise<void> {
    const base = `/api/flows/${encodeURIComponent(flowId)}/trace/session/${encodeURIComponent(sessionId)}`;
    return request(runtimeId ? `${base}?runtimeId=${encodeURIComponent(runtimeId)}` : base, { method: 'DELETE' });
  },

  /** Relative URL for the live-trace SSE stream. The backend injects the
   *  runtime bearer; the browser just opens an `EventSource` against it.
   *  When `runtimeId` is omitted, targets the default runtime. */
  traceStreamUrl(flowId: string, runtimeId?: string): string {
    const base = `/api/flows/${encodeURIComponent(flowId)}/trace/stream`;
    return runtimeId ? `${base}?runtimeId=${encodeURIComponent(runtimeId)}` : base;
  },

  // ─── Flow testing framework (DX flow-testing) ───────────────────────

  /** Read a flow's saved test suite (empty suite when it has none). */
  flowTests(id: string): Promise<import('./next/flowTests').FlowTestSuite> {
    return request(`/api/flows/${encodeURIComponent(id)}/tests`);
  },

  /** Persist a flow's test suite (writes tests.json + exports flow.tests.yaml). */
  saveFlowTests(id: string, suite: import('./next/flowTests').FlowTestSuite): Promise<{ ok: true }> {
    return request(`/api/flows/${encodeURIComponent(id)}/tests`, { method: 'PUT', body: JSON.stringify(suite) });
  },

  /** Re-import tests.json from a hand-edited flow.tests.yaml. */
  importFlowTests(id: string): Promise<import('./next/flowTests').FlowTestSuite> {
    return request(`/api/flows/${encodeURIComponent(id)}/tests/import`, { method: 'POST' });
  },

  /** Run a flow's tests against a runtime (defaults to the local/dev runtime
   *  when `runtimeId` is given). */
  runFlowTests(id: string, opts?: { testId?: string; runtimeId?: string }): Promise<import('./next/flowTests').SuiteResult> {
    const q = new URLSearchParams();
    if (opts?.testId) q.set('testId', opts.testId);
    if (opts?.runtimeId) q.set('runtimeId', opts.runtimeId);
    const qs = q.toString();
    return request(`/api/flows/${encodeURIComponent(id)}/tests/run${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },

  // --- Local dev runtime (dev-mode only; 404 when disabled) ---
  localRuntimeStatus(): Promise<{ running: boolean; runtimeId: string; port: number; healthy: boolean }> {
    return request('/api/local-runtime/status');
  },
  localRuntimeStart(): Promise<{ running: boolean; runtimeId: string; port: number; healthy: boolean }> {
    return request('/api/local-runtime/start', { method: 'POST' });
  },
  localRuntimeStop(): Promise<void> {
    return request('/api/local-runtime/stop', { method: 'POST' });
  },

  /** SLO summary for one flow over the trailing 24 h. Backend proxies a
   *  set of Prometheus instant-queries; returns 503 (rendered as the
   *  "Prometheus not configured" empty state) when prometheusUrl isn't
   *  set on the backend. */
  flowSlo(id: string): Promise<FlowSlo> {
    return request<FlowSlo>(`/api/flows/${encodeURIComponent(id)}/slo`);
  },

  /** Operator-configured Grafana / Tempo URLs. Used by Live Trace to
   *  build "View trace" deep-links and by the Flow detail page to link
   *  out to a Grafana dashboard. Both fields may be null. */
  observabilityConfig(): Promise<ObservabilityConfig> {
    return request<ObservabilityConfig>('/api/observability/config');
  },

  /** Per-runtime deployment matrix for a flow.
   *  Backend fans out to every registered runtime and reports each
   *  one's sync state (in-sync / stale / not-deployed / unreachable). */
  flowDeployments(id: string): Promise<FlowDeploymentsResponse> {
    return request<FlowDeploymentsResponse>(
      `/api/flows/${encodeURIComponent(id)}/deployments`
    );
  },

  /** Bulk version: deployments for every flow keyed by flow id. Used by
   *  the flow list to render env-tracks without N round-trips. */
  allFlowDeployments(): Promise<Record<string, FlowDeploymentsResponse>> {
    return request<Record<string, FlowDeploymentsResponse>>(
      '/api/flows/_deployments'
    );
  },

  /** flow-event-binding US-003: list flows that reference a given Pulsar
   *  topic. Returned rows say whether each flow publishes (producer) or
   *  subscribes (consumer), plus the subscription name when present.
   *  The event-design canvas calls this once per topic to auto-render
   *  ProducerRef / ConsumerRef nodes. */
  flowsByTopic(
    topicFullName: string,
  ): Promise<Array<{ flowId: string; flowName: string; role: 'producer' | 'consumer'; subscriptionName?: string }>> {
    return request(
      `/api/flows/_by-topic?topic=${encodeURIComponent(topicFullName)}`,
    );
  },

  /** Promote a flow to the next environment in its transport chain.
   *  Only succeeds when an upstream runtime (one whose `promotesTo`
   *  equals `targetRuntimeId`) currently has the flow in-sync. */
  promoteFlow(id: string, targetRuntimeId: string): Promise<PromoteFlowResponse> {
    return request<PromoteFlowResponse>(
      `/api/flows/${encodeURIComponent(id)}/promote`,
      {
        method: 'POST',
        body: JSON.stringify({ targetRuntimeId }),
      }
    );
  },

  valueMappings(): Promise<ValueMappingSummary[]> {
    return request<ValueMappingSummary[]>('/api/value-mappings');
  },

  valueMapping(id: string): Promise<ValueMappingDoc> {
    return request<ValueMappingDoc>(`/api/value-mappings/${encodeURIComponent(id)}`);
  },

  saveValueMapping(
    id: string,
    body: { name?: string; rows: Array<{ key: string; value: string }> }
  ): Promise<ValueMappingDoc> {
    return request<ValueMappingDoc>(`/api/value-mappings/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteValueMapping(id: string): Promise<void> {
    return request<void>(`/api/value-mappings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  deployValueMapping(id: string): Promise<{ ok: boolean; deployedAt?: string; rowCount?: number; error?: string; status?: number; detail?: string }> {
    return request(`/api/value-mappings/${encodeURIComponent(id)}/deploy`, {
      method: 'POST',
    });
  },

  messageMappings(): Promise<MessageMappingSummary[]> {
    return request<MessageMappingSummary[]>('/api/message-mappings');
  },

  messageMapping(id: string): Promise<MessageMappingDoc> {
    return request<MessageMappingDoc>(
      `/api/message-mappings/${encodeURIComponent(id)}`
    );
  },

  saveMessageMapping(
    id: string,
    body: Partial<MessageMappingDoc> & { name?: string }
  ): Promise<MessageMappingDoc> {
    return request<MessageMappingDoc>(
      `/api/message-mappings/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      }
    );
  },

  deleteMessageMapping(id: string): Promise<void> {
    return request<void>(`/api/message-mappings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  deployMessageMapping(
    id: string
  ): Promise<{ ok: boolean; deployedAt?: string; error?: string; status?: number; detail?: string }> {
    return request(`/api/message-mappings/${encodeURIComponent(id)}/deploy`, {
      method: 'POST',
    });
  },

  // ─── Multi-runtime registry ────────────────────────────────────────

  listRuntimes(): Promise<RuntimeSummary[]> {
    return request<RuntimeSummary[]>('/api/runtimes');
  },

  /** Designer-wide runtime metadata (Pipe self-update Task 3) — exposes
   *  `LATEST_RUNTIME_VERSION` so the SPA's version badge can compare each
   *  pipe's reported `pipeMetadata.runtimeVersion` against the latest stamp
   *  without redeploying the SPA on every patch. */
  runtimesMeta(): Promise<{ latestRuntimeVersion: string }> {
    return request<{ latestRuntimeVersion: string }>('/api/runtimes/_meta');
  },

  /** Cross-product matrix of registered runtimes × flows with per-cell
   *  deploy state. Backed by `GET /api/runtimes/_deployments-matrix`
   *  (Pipes Plan D Task 4). When `environment` is supplied the runtime
   *  set is narrowed to that env. */
  deploymentsMatrix(environment?: string): Promise<DeploymentsMatrix> {
    const q = environment ? `?environment=${encodeURIComponent(environment)}` : '';
    return request<DeploymentsMatrix>(`/api/runtimes/_deployments-matrix${q}`);
  },

  getRuntime(id: string): Promise<RuntimeRecord> {
    return request<RuntimeRecord>(`/api/runtimes/${encodeURIComponent(id)}`);
  },

  createRuntime(body: CreateRuntimeBody): Promise<RuntimeRecord> {
    return request<RuntimeRecord>('/api/runtimes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Create a pipe-mode runtime registration. No URL or deploy token is
   *  collected up-front — the runtime phones home using the bundle
   *  generated by `generatePipeBundle()`. */
  createPipeRuntime(body: {
    name: string;
    environment?: RuntimeEnvironment;
    labels?: string[];
    isDefault?: boolean;
    pulsarCluster?: string;
  }): Promise<RuntimeSummary> {
    return request<RuntimeSummary>('/api/runtimes/pipe', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** The one-line `docker run` that starts a Pipe (Pipebase Cloud G §3).
   *  Each call mints a new Pipe token; the previous command stops working. */
  pipeStartCommand(runtimeId: string, publishHttp: boolean): Promise<{ runtimeId: string; image: string; command: string }> {
    return request(`/api/runtimes/${encodeURIComponent(runtimeId)}/start-command`, {
      method: 'POST',
      body: JSON.stringify({ publishHttp }),
    });
  },

  /** The workspace's data, encrypted with `passphrase` (owner only). The
   *  docs site's open-pipebase-export.mjs opens it. Binary, so `fetch`
   *  directly, as for a bundle. */
  async exportWorkspace(workspaceId: string, passphrase: string): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/export`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ passphrase }),
    });
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => undefined);
      }
      throw new ApiError(res.status, extractErrorMessage(body, res.status), body);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'pipebase-export.bin';
    return { blob: await res.blob(), filename: name };
  },

  /** Triggers a bundle regeneration. Returns the binary as a Blob so the
   *  caller can offer it as a download. The cleartext PIPEBASE_PIPE_TOKEN
   *  is baked into the bundle and never returned separately by the API.
   *
   *  Uses `fetch` directly because the response is binary — the shared
   *  `request<T>` helper assumes JSON. */
  async generatePipeBundle(runtimeId: string): Promise<Blob> {
    const res = await fetch(
      withWorkspaceQuery(`/api/runtimes/${encodeURIComponent(runtimeId)}/bundle`),
      { method: 'POST' },
    );
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => undefined);
      }
      throw new ApiError(res.status, extractErrorMessage(body, res.status), body);
    }
    return await res.blob();
  },

  updateRuntime(id: string, body: UpdateRuntimeBody): Promise<RuntimeRecord> {
    return request<RuntimeRecord>(`/api/runtimes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteRuntime(id: string): Promise<void> {
    return request<void>(`/api/runtimes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  /** Live health for a single runtime — never throws on unreachable, the
   *  backend always returns 200 with `reachable: false` set. */
  runtimeHealth(id: string): Promise<RuntimeHealth> {
    return request<RuntimeHealth>(`/api/runtimes/${encodeURIComponent(id)}/health`);
  },

  /** Drift snapshot for one runtime — Pipes Plan E. The backend computes
   *  this from the designer's deployment record vs the runtime's last
   *  reported deployedFlows[] set. Empty arrays = in sync. */
  runtimeDrift(runtimeId: string): Promise<RuntimeDriftStatus> {
    return request<RuntimeDriftStatus>(`/api/runtimes/${encodeURIComponent(runtimeId)}/drift`);
  },

  /** Fleet-wide drift summary in ONE call — every runtime's derived drift
   *  status plus rolled-up counts. Replaces fanning out N per-runtime
   *  `runtimeDrift()` calls from the Fleet matrix page. Optionally scoped
   *  to one environment. */
  driftSummary(environment?: string): Promise<DriftSummary> {
    const qs = environment ? `?environment=${encodeURIComponent(environment)}` : '';
    return request<DriftSummary>(`/api/runtimes/_drift-summary${qs}`);
  },

  /** Re-deploys flows listed as missing in the runtime's latest drift
   *  snapshot. Unexpected flows are intentionally left alone. */
  repairDrift(runtimeId: string): Promise<RepairDriftResult> {
    return request<RepairDriftResult>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/repair-drift`,
      { method: 'POST' },
    );
  },

  /** Re-deploys every flow in runtime.deployedFlows regardless of drift
   *  snapshot state — guaranteed clean slate after a restart. */
  fullSync(runtimeId: string): Promise<FullSyncResult> {
    return request<FullSyncResult>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/full-sync`,
      { method: 'POST' },
    );
  },

  /** Pipe tunnel lifecycle event log — newest first. Returns an empty array
   *  when no events have been recorded yet or the Pipe has never connected.
   *  `limit` defaults to 100 on the server side. */
  runtimeConnectionLog(runtimeId: string, limit?: number): Promise<{ events: PipeConnectionEvent[] }> {
    const qs = limit !== undefined ? `?limit=${limit}` : '';
    return request<{ events: PipeConnectionEvent[] }>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/connection-log${qs}`,
    );
  },

  /** Trigger a self-update on a Pipe — the designer forwards the request
   *  over the existing tunnel; the Pipe writes an update-trigger file the
   *  sidecar updater picks up. Image must match
   *  `ghcr.io/jobu279/pipebase-runtime:<tag>` (enforced server-side too).
   *  Returns 400 for inbound runtimes or bad images, 503 when the Pipe
   *  is offline, and 502 when the runtime/tunnel rejects the request. */
  updatePipeRuntime(runtimeId: string, image: string): Promise<{ ok: boolean; image: string }> {
    return request<{ ok: boolean; image: string }>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/update`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image }),
      },
    );
  },

  /** Provision a brand-new managed runtime container. Backend creates it
   *  via the Docker daemon (must have PIPEBASE_RUNTIME_ROOT_DOMAIN set + the
   *  Docker socket mounted). Returns 503 if the feature isn't enabled. */
  provisionRuntime(body: ProvisionRuntimeBody): Promise<RuntimeRecord> {
    return request<RuntimeRecord>('/api/runtimes/managed', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** List `ipaas-runtime:*` image tags present on the Docker host.
   *  Used by the provision dialog so the user can only pick what's
   *  deployable. Returns `{ images: [] }` when the managed-runtime
   *  feature is disabled. */
  runtimeImages(): Promise<{ images: string[]; defaultImage?: string; configured?: string; error?: string }> {
    return request('/api/runtimes/images');
  },

  /** Live container state from `docker inspect`. Returns `{managed:false}`
   *  for externally-managed runtimes. */
  runtimeContainer(id: string): Promise<RuntimeContainerState> {
    return request<RuntimeContainerState>(`/api/runtimes/${encodeURIComponent(id)}/container`);
  },

  startRuntime(id: string): Promise<{ ok: boolean }> {
    return request(`/api/runtimes/${encodeURIComponent(id)}/start`, { method: 'POST' });
  },

  stopRuntime(id: string): Promise<{ ok: boolean }> {
    return request(`/api/runtimes/${encodeURIComponent(id)}/stop`, { method: 'POST' });
  },

  restartRuntime(id: string): Promise<{ ok: boolean }> {
    return request(`/api/runtimes/${encodeURIComponent(id)}/restart`, { method: 'POST' });
  },

  /** Deploy a flow to a specific runtime by id (bypasses the default). */
  deployToRuntime(flowId: string, runtimeId: string): Promise<DeployResult & { runtimeId?: string }> {
    return request<DeployResult>(
      `/api/flows/${encodeURIComponent(flowId)}/deploy?runtimeId=${encodeURIComponent(runtimeId)}`,
      { method: 'POST' },
    );
  },

  /** Fan-out deploy: deploy the same flow to multiple runtimes in a single
   *  request. The backend dispatches independently per target — failures on
   *  one runtime do not abort the rest. Returns `{deployed, failed}`. */
  deployToMany(flowId: string, runtimeIds: string[]): Promise<DeployToManyResult> {
    return request<DeployToManyResult>(
      `/api/flows/${encodeURIComponent(flowId)}/deploy-to-many`,
      {
        method: 'POST',
        body: JSON.stringify({ runtimeIds }),
      },
    );
  },

  // ─── Pools (Pipes V2) ──────────────────────────────────────────────

  listPools(): Promise<PoolSummary[]> {
    return request<PoolSummary[]>('/api/pools');
  },

  getPool(id: string): Promise<PoolSummary> {
    return request<PoolSummary>(`/api/pools/${encodeURIComponent(id)}`);
  },

  /** Pool members are ordinary pipe-mode RuntimeSummary records carrying
   *  the pool id in their `poolId` field. Filters server-returned runtimes
   *  client-side — same scope as listRuntimes (workspace). */
  async listPoolMembers(poolId: string): Promise<RuntimeSummary[]> {
    const all = await api.listRuntimes();
    return all.filter((r) => r.poolId === poolId);
  },

  createPool(body: CreatePoolBody): Promise<PoolSummary> {
    return request<PoolSummary>('/api/pools', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updatePool(id: string, body: UpdatePoolBody): Promise<PoolSummary> {
    return request<PoolSummary>(`/api/pools/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deletePool(id: string): Promise<void> {
    return request<void>(`/api/pools/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  async generatePoolBundle(poolId: string): Promise<Blob> {
    const res = await fetch(
      withWorkspaceQuery(`/api/pools/${encodeURIComponent(poolId)}/bundle`),
      { method: 'POST' },
    );
    if (!res.ok) {
      let body: unknown;
      try { body = await res.json(); }
      catch { body = await res.text().catch(() => undefined); }
      throw new ApiError(res.status, extractErrorMessage(body, res.status), body);
    }
    return await res.blob();
  },

  /** Would deploying this flow to the pool make it fire once per member? */
  poolDeployCheck(
    poolId: string,
    flowId: string,
  ): Promise<{ warnings: DuplicateFiringWarning[] }> {
    return request<{ warnings: DuplicateFiringWarning[] }>(
      `/api/pools/${encodeURIComponent(poolId)}/deploy-check?flowId=${encodeURIComponent(flowId)}`,
    );
  },

  deployToPool(poolId: string, flowId: string): Promise<PoolDeployResult> {
    return request<PoolDeployResult>(
      `/api/pools/${encodeURIComponent(poolId)}/deploy`,
      { method: 'POST', body: JSON.stringify({ flowId } satisfies PoolDeployBody) },
    );
  },

  undeployFromPool(poolId: string, flowId: string): Promise<PoolDeployResult> {
    return request<PoolDeployResult>(
      `/api/pools/${encodeURIComponent(poolId)}/undeploy`,
      { method: 'POST', body: JSON.stringify({ flowId } satisfies PoolDeployBody) },
    );
  },

  async getPoolMetrics(poolId: string): Promise<string> {
    const res = await fetch(
      withWorkspaceQuery(`/api/pools/${encodeURIComponent(poolId)}/metrics`),
    );
    if (!res.ok) {
      let body: unknown;
      try { body = await res.json(); }
      catch { body = await res.text().catch(() => undefined); }
      throw new ApiError(res.status, extractErrorMessage(body, res.status), body);
    }
    return await res.text();
  },

  // ─── Externalized parameters ───────────────────────────────────────

  listParameters(): Promise<ParameterSummary[]> {
    return request<ParameterSummary[]>('/api/parameters');
  },

  getParameter(id: string): Promise<ParameterSummary> {
    return request<ParameterSummary>(`/api/parameters/${encodeURIComponent(id)}`);
  },

  createParameter(body: CreateParameterBody): Promise<ParameterSummary> {
    return request<ParameterSummary>('/api/parameters', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateParameter(id: string, body: UpdateParameterBody): Promise<ParameterSummary> {
    return request<ParameterSummary>(`/api/parameters/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteParameter(id: string, force = false): Promise<void> {
    const q = force ? '?force=true' : '';
    return request<void>(`/api/parameters/${encodeURIComponent(id)}${q}`, {
      method: 'DELETE',
    });
  },

  /** Preview the merged parameter map for an upcoming deploy. */
  resolvedParameters(opts: { runtimeId?: string; flowId?: string } = {}): Promise<ResolvedParameters> {
    const q = new URLSearchParams();
    if (opts.runtimeId) q.set('runtimeId', opts.runtimeId);
    if (opts.flowId) q.set('flowId', opts.flowId);
    const qs = q.toString();
    return request<ResolvedParameters>(`/api/parameters/resolved${qs ? '?' + qs : ''}`);
  },

  // ─── Policies (spec 2026-11-02) ────────────────────────────────────

  listPolicies(): Promise<{ policies: Policy[] }> {
    return request<{ policies: Policy[] }>('/api/policies');
  },

  createPolicy(body: PolicyInput): Promise<Policy> {
    return request<Policy>('/api/policies', { method: 'POST', body: JSON.stringify(body) });
  },

  updatePolicy(id: string, body: PolicyInput): Promise<Policy> {
    return request<Policy>(`/api/policies/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) });
  },

  deletePolicy(id: string): Promise<void> {
    return request<void>(`/api/policies/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /** What the policies would say about deploying this flow, without
   *  deploying or counting it. */
  policyCheck(flowId: string, runtimeId?: string | null): Promise<PolicyCheckResult> {
    const q = runtimeId ? `?runtimeId=${encodeURIComponent(runtimeId)}` : '';
    return request<PolicyCheckResult>(`/api/flows/${encodeURIComponent(flowId)}/policy-check${q}`, { method: 'POST' });
  },

  // ─── Roles ─────────────────────────────────────────────────────────

  listRoles(): Promise<Role[]> {
    return request<Role[]>('/api/roles');
  },

  getRole(id: string): Promise<Role> {
    return request<Role>(`/api/roles/${encodeURIComponent(id)}`);
  },

  createRole(body: CreateRoleBody): Promise<Role> {
    return request<Role>('/api/roles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateRole(id: string, body: UpdateRoleBody): Promise<Role> {
    return request<Role>(`/api/roles/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /** 409 returned if the role is bound to one or more OAuth clients,
   *  unless `force` is true. */
  deleteRole(id: string, force = false): Promise<void> {
    const q = force ? '?force=true' : '';
    return request<void>(`/api/roles/${encodeURIComponent(id)}${q}`, {
      method: 'DELETE',
    });
  },

  // ─── OAuth clients ────────────────────────────────────────────────

  listOAuthClients(): Promise<OAuthClientSummary[]> {
    return request<OAuthClientSummary[]>('/api/oauth-clients');
  },

  getOAuthClient(id: string): Promise<OAuthClientSummary> {
    return request<OAuthClientSummary>(`/api/oauth-clients/${encodeURIComponent(id)}`);
  },

  /** Returns the plaintext `secret` ONCE — the only time it ever leaves
   *  the backend. Subsequent GETs never expose it. */
  createOAuthClient(body: CreateOAuthClientBody): Promise<CreateOAuthClientResponse> {
    return request<CreateOAuthClientResponse>('/api/oauth-clients', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** When the body includes a non-empty `secret`, returns
   *  `CreateOAuthClientResponse` (plaintext secret shown once). Otherwise
   *  returns the regular `OAuthClientSummary`. The caller can detect a
   *  rotated secret by checking `'secret' in result`. */
  updateOAuthClient(
    id: string,
    body: UpdateOAuthClientBody,
  ): Promise<OAuthClientSummary | CreateOAuthClientResponse> {
    return request<OAuthClientSummary | CreateOAuthClientResponse>(
      `/api/oauth-clients/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },

  deleteOAuthClient(id: string): Promise<void> {
    return request<void>(`/api/oauth-clients/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ─── Phase 4: cross-runtime message monitor ───────────────────────

  listMessages(query: MessageQuery = {}): Promise<MessageListResponse> {
    const q = new URLSearchParams();
    if (query.runtimeId) q.set('runtimeId', query.runtimeId);
    if (query.flowId) q.set('flowId', query.flowId);
    if (query.status) q.set('status', query.status);
    if (query.since) q.set('since', query.since);
    if (query.limit != null) q.set('limit', String(query.limit));
    const qs = q.toString();
    return request<MessageListResponse>(`/api/messages${qs ? '?' + qs : ''}`);
  },

  messageStats(
    opts: { since?: string; bucketMinutes?: number } = {},
  ): Promise<MessageStatsResponse> {
    const q = new URLSearchParams();
    if (opts.since) q.set('since', opts.since);
    if (opts.bucketMinutes != null) q.set('bucketMinutes', String(opts.bucketMinutes));
    const qs = q.toString();
    return request<MessageStatsResponse>(`/api/messages/stats${qs ? '?' + qs : ''}`);
  },

  // ── Connections ──────────────────────────────────────────────────────

  listConnections(): Promise<ConnectionRecord[]> {
    return request<ConnectionRecord[]>('/api/connections');
  },

  createConnection(body: ConnectionInputBody): Promise<ConnectionRecord> {
    return request<ConnectionRecord>('/api/connections', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateConnection(
    id: string,
    body: Partial<ConnectionInputBody>,
  ): Promise<ConnectionRecord> {
    return request<ConnectionRecord>(
      `/api/connections/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  deleteConnection(id: string): Promise<void> {
    return request<void>(`/api/connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  connectionHealth(): Promise<Record<string, ConnectionHealth>> {
    return request<Record<string, ConnectionHealth>>('/api/connections/health');
  },

  // ── Security / TLS health (US-007) ───────────────────────────────────
  securityTls(): Promise<TlsHealthResponse> {
    return request<TlsHealthResponse>('/api/security/tls');
  },

  // ── Authentication / SSO ─────────────────────────────────────────────

  /** Current session's user. `user: null` when not logged in. */
  authMe(): Promise<AuthMeResponse> {
    return request<AuthMeResponse>('/api/auth/me');
  },

  /** SSO configuration (no secret in the response). */
  ssoConfigGet(): Promise<SsoConfigResponse> {
    return request<SsoConfigResponse>('/api/auth/sso/config');
  },

  ssoConfigSave(body: SsoConfigInput): Promise<SsoConfigResponse> {
    return request<SsoConfigResponse>('/api/auth/sso/config', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  ssoConfigClear(): Promise<void> {
    return request<void>('/api/auth/sso/config', { method: 'DELETE' });
  },

  // ── SSO users (admin-only) ─────────────────────────────────────────
  ssoUsers(): Promise<{ users: import('./types').SsoUserSummary[] }> {
    return request('/api/auth/sso/users');
  },
  setSsoUserRole(oid: string, role: 'admin' | 'member'): Promise<{ user: unknown }> {
    return request(`/api/auth/sso/users/${encodeURIComponent(oid)}/role`, { method: 'PUT', body: JSON.stringify({ role }) });
  },
  setSsoUserRoles(oid: string, roles: string[]): Promise<{ user: unknown }> {
    return request(`/api/auth/sso/users/${encodeURIComponent(oid)}/roles`, { method: 'PUT', body: JSON.stringify({ roles }) });
  },
  unlockSsoUser(oid: string): Promise<{ user: unknown }> {
    return request(`/api/auth/sso/users/${encodeURIComponent(oid)}/unlock`, { method: 'POST' });
  },

  /** Read the setup/enforcement state — drives the first-admin wizard and
   *  the unauthenticated login gate. */
  authSetupState(): Promise<import('./types').SetupStateResponse> {
    return request('/api/auth/setup-state');
  },

  /** Create the first local admin account (first-run only). Returns the
   *  freshly-authenticated principal. */
  authSetup(username: string, password: string): Promise<AuthMeResponse> {
    return request('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  /** Log in with a local username/password. Throws `ApiError` on 401
   *  (bad credentials) / 429 (rate-limited) / 423 (locked). */
  authLogin(username: string, password: string): Promise<AuthMeResponse> {
    return request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  /** Generic logout — clears the session cookie regardless of auth kind. */
  authLogout(): Promise<{ ok: boolean }> {
    return request('/api/auth/logout', { method: 'POST' });
  },

  /** SSO logout. Returns the Azure AD logout URL the SPA should navigate
   *  to next so the IdP also clears its session. */
  authLogoutSso(): Promise<{ ok: boolean; redirectTo?: string }> {
    return request('/api/auth/sso/logout', { method: 'POST' });
  },

  // ── Local account admin (Local Authentication, Task 8) ───────────────
  // Admin-only CRUD over local username/password accounts, plus the
  // self-service "change my password" endpoint. Last-admin / self-target
  // guards are enforced server-side (409/400) — the SPA disables the
  // offending controls as UX and still surfaces a slipped-through error.

  localUsers(): Promise<{ users: import('./types').LocalUserSummary[] }> {
    return request('/api/auth/local/users');
  },
  createLocalUser(b: {
    username: string;
    password: string;
    role: 'admin' | 'member';
    workspace?: { id: string; role: WorkspaceRole };
  }): Promise<{ user: import('./types').LocalUserSummary; warning?: string }> {
    return request('/api/auth/local/users', { method: 'POST', body: JSON.stringify(b) });
  },
  setLocalUserRole(id: string, role: 'admin' | 'member'): Promise<{ ok: true }> {
    return request(`/api/auth/local/users/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ role }) });
  },
  resetLocalUserPassword(id: string, password: string): Promise<{ ok: true }> {
    return request(`/api/auth/local/users/${encodeURIComponent(id)}/password`, { method: 'POST', body: JSON.stringify({ password }) });
  },
  lockLocalUser(id: string): Promise<{ ok: true }> {
    return request(`/api/auth/local/users/${encodeURIComponent(id)}/lock`, { method: 'POST' });
  },
  unlockLocalUser(id: string): Promise<{ ok: true }> {
    return request(`/api/auth/local/users/${encodeURIComponent(id)}/unlock`, { method: 'POST' });
  },
  deleteLocalUser(id: string): Promise<void> {
    return request(`/api/auth/local/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  changeMyPassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request('/api/auth/local/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });
  },

  // ─── Event Designs (Pulsar topology) ────────────────────────────────

  listEventDesigns(): Promise<EventDesignSummary[]> {
    return request<EventDesignSummary[]>('/api/event-designs');
  },

  getEventDesign(id: string): Promise<EventDesignDocument> {
    return request<EventDesignDocument>(
      `/api/event-designs/${encodeURIComponent(id)}`,
    );
  },

  createEventDesign(body: CreateEventDesignBody): Promise<EventDesignDocument> {
    return request<EventDesignDocument>('/api/event-designs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  saveEventDesign(
    id: string,
    body: PutEventDesignBody,
  ): Promise<EventDesignDocument> {
    return request<EventDesignDocument>(
      `/api/event-designs/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },

  deleteEventDesign(id: string): Promise<void> {
    return request<void>(`/api/event-designs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  applyEventDesign(
    id: string,
    runtimeId?: string,
  ): Promise<ApplyEventDesignResponse> {
    return request<ApplyEventDesignResponse>(
      `/api/event-designs/${encodeURIComponent(id)}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(runtimeId ? { runtimeId } : {}),
      },
    );
  },

  eventDesignTopicOwners(): Promise<TopicOwnersMap> {
    return request<TopicOwnersMap>('/api/event-designs/topic-owners');
  },

  /**
   * Discover from broker (event-design-features US-009).
   * Backend scans the configured Pulsar admin URL and returns the id of a
   * newly-created event design containing the discovered topology.
   */
  discoverEventDesign(body: DiscoverFromBrokerBody): Promise<DiscoverFromBrokerResponse> {
    return request<DiscoverFromBrokerResponse>('/api/event-designs/discover', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** AsyncAPI 3.0 import (asyncapi-export-import US-006). Creates or
   *  previews-merge an event design from a YAML/JSON spec. */
  importAsyncApi(body: {
    asyncapi: string;
    mode: 'create' | 'merge';
    targetId?: string;
  }): Promise<{
    ok: true;
    mode: 'create' | 'merge';
    id: string;
    warnings: Array<{ code: string; message: string; path?: string }>;
    diff?: {
      addedTopics: string[];
      removedTopics: string[];
      changedTopics: Array<{ key: string; changes: string[] }>;
    };
    proposedTopology?: unknown;
  }> {
    return request('/api/event-designs/import', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** List every topic across every event design — feeds the pulsar:listener
   *  / pulsar:publish topic typeahead picker (flow-event-binding loop). */
  eventDesignTopics(): Promise<EventDesignTopicListItem[]> {
    return request<EventDesignTopicListItem[]>('/api/event-designs/topics');
  },

  /** Aggregated topic catalog across every event design with cross-
   *  referenced producer + consumer flows (event-portal-governance US-006). */
  eventCatalog(): Promise<TopicCatalogEntry[]> {
    return request<TopicCatalogEntry[]>('/api/event-catalog');
  },

  // ── SAP event-template catalogue (sap-event-catalog US-007/008) ────
  listEventTemplates(): Promise<import('./types.js').TemplateSummary[]> {
    return request<import('./types.js').TemplateSummary[]>('/api/event-templates');
  },

  getEventTemplate(id: string): Promise<import('./types.js').TemplateDetail> {
    return request<import('./types.js').TemplateDetail>(
      `/api/event-templates/${id
        .split('/')
        .map((s) => encodeURIComponent(s))
        .join('/')}`,
    );
  },

  importEventTemplate(
    id: string,
    body: { name?: string; workspaceId?: string },
  ): Promise<import('./types.js').TemplateImportResponse> {
    return request<import('./types.js').TemplateImportResponse>(
      `/api/event-templates/${id
        .split('/')
        .map((s) => encodeURIComponent(s))
        .join('/')}/import`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  // ── Pulsar mesh ──────────────────────────────────────────────────────
  // Backend returns 503 with `{error:"Pulsar is not configured"}` when the
  // designer container has no PIPEBASE_PULSAR_ADMIN_URL set; the Mesh page
  // catches that ApiError to render its empty state.

  pulsarClusters(): Promise<PulsarCluster[]> {
    return request<PulsarCluster[]>('/api/pulsar/clusters');
  },

  pulsarTopics(namespace?: string): Promise<PulsarTopicSummary[]> {
    const qs = namespace ? `?namespace=${encodeURIComponent(namespace)}` : '';
    return request<PulsarTopicSummary[]>(`/api/pulsar/topics${qs}`);
  },

  pulsarTopicStats(fqn: string): Promise<PulsarTopicStats> {
    return request<PulsarTopicStats>(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/stats`,
    );
  },

  pulsarTopicSubscriptions(fqn: string): Promise<PulsarSubscription[]> {
    return request<PulsarSubscription[]>(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/subscriptions`,
    );
  },

  createPulsarTopic(
    body: CreateTopicBody,
  ): Promise<{ ok: true; fqn: string; partitions: number }> {
    return request('/api/pulsar/topics', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  setPulsarReplication(
    fqn: string,
    clusters: string[],
  ): Promise<{ ok: true; fqn: string; clusters: string[] }> {
    return request(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/replication`,
      { method: 'PUT', body: JSON.stringify({ clusters }) },
    );
  },

  /** Fase 4 B: flat list of every topic that has a schema.
   *  Powers the /schemas browser page. */
  pulsarSchemas(): Promise<PulsarSchemaEntry[]> {
    return request<PulsarSchemaEntry[]>('/api/pulsar/schemas');
  },

  /** Fase 4 B: full version history for a single topic's schema. */
  pulsarTopicSchemaVersions(fqn: string): Promise<PulsarSchemaVersion[]> {
    return request<PulsarSchemaVersion[]>(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/schemas`,
    );
  },

  // ── Pulsar tenants (tenant-vpn-management) ──────────────────────────

  pulsarTenants(): Promise<PulsarTenantSummary[]> {
    return request<PulsarTenantSummary[]>('/api/pulsar/tenants');
  },

  pulsarTenant(name: string): Promise<PulsarTenantDetail> {
    return request<PulsarTenantDetail>(
      `/api/pulsar/tenants/${encodeURIComponent(name)}`,
    );
  },

  createPulsarTenant(body: CreatePulsarTenantBody): Promise<{ ok: true; name: string }> {
    return request('/api/pulsar/tenants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updatePulsarTenant(
    name: string,
    body: UpdatePulsarTenantBody,
  ): Promise<{ ok: true; name: string }> {
    return request(`/api/pulsar/tenants/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /** Pass confirm = the exact tenant name (server enforces match). */
  deletePulsarTenant(
    name: string,
    confirm: string,
    force = false,
  ): Promise<{ ok: true; name: string; forced: boolean }> {
    const qs = new URLSearchParams({ confirm });
    if (force) qs.set('force', 'true');
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}?${qs.toString()}`,
      { method: 'DELETE' },
    );
  },

  /** The tenant's change history, newest first. Admin-only (403 otherwise). */
  pulsarTenantAudit(name: string): Promise<PulsarTenantAuditEntry[]> {
    return request<PulsarTenantAuditEntry[]>(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/audit`,
    );
  },

  pulsarTenantNamespaces(name: string): Promise<string[]> {
    return request<string[]>(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces`,
    );
  },

  createPulsarTenantNamespace(
    name: string,
    namespace: string,
  ): Promise<{ ok: true; namespace: string }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces`,
      { method: 'POST', body: JSON.stringify({ namespace }) },
    );
  },

  pulsarTenantNamespaceQuotas(
    name: string,
    ns: string,
  ): Promise<NamespaceQuotaInfo> {
    return request<NamespaceQuotaInfo>(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/quotas`,
    );
  },

  setPulsarTenantRetention(
    name: string,
    ns: string,
    body: { retentionTimeInMinutes: number; retentionSizeInMB: number },
  ): Promise<{ ok: true }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/retention`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  setPulsarTenantMessageTtl(
    name: string,
    ns: string,
    messageTTL: number,
  ): Promise<{ ok: true }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/message-ttl`,
      { method: 'PUT', body: JSON.stringify({ messageTTL }) },
    );
  },

  setPulsarTenantBacklogQuota(
    name: string,
    ns: string,
    body: { limitSize: number; limitTime?: number; policy: string; backlogQuotaType?: string },
  ): Promise<{ ok: true }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/backlog-quota`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  pulsarTenantPermissions(
    name: string,
    ns: string,
  ): Promise<Record<string, string[]>> {
    return request<Record<string, string[]>>(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/permissions`,
    );
  },

  grantPulsarTenantPermission(
    name: string,
    ns: string,
    role: string,
    actions: string[],
  ): Promise<{ ok: true }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/permissions/${encodeURIComponent(role)}`,
      { method: 'PUT', body: JSON.stringify({ actions }) },
    );
  },

  revokePulsarTenantPermission(
    name: string,
    ns: string,
    role: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/pulsar/tenants/${encodeURIComponent(name)}/namespaces/${encodeURIComponent(ns)}/permissions/${encodeURIComponent(role)}`,
      { method: 'DELETE' },
    );
  },

  /** Fase 4 C: rewind a subscription to a point-in-time (epoch-millis).
   *  Pass 0 to rewind to start-of-retention. Returns 409 when consumers
   *  are still connected — the SPA's event-replay UI surfaces that as a
   *  "disconnect consumers first" message. */
  resetPulsarCursor(
    fqn: string,
    subscription: string,
    pointInTimeMs: number,
  ): Promise<{ ok: true; fqn: string; subscription: string; pointInTimeMs: number }> {
    return request(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/subscriptions/${encodeURIComponent(subscription)}/reset-cursor`,
      { method: 'POST', body: JSON.stringify({ pointInTimeMs }) },
    );
  },

  /** Replay a subscription (message-replay-ui US-001). The
   *  `confirmation` field MUST equal `fqn` — both the SPA's ReplayModal
   *  and the backend enforce this. */
  replayPulsarSubscription(
    fqn: string,
    subscription: string,
    body: {
      anchor: 'earliest' | 'timestamp' | 'message-id';
      timestamp?: string;
      messageId?: string;
      confirmation: string;
    },
  ): Promise<{ ok: true; fqn: string; subscription: string; anchor: string }> {
    return request(
      `/api/pulsar/topics/${encodeURIComponent(fqn)}/subscriptions/${encodeURIComponent(subscription)}/replay`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /** List explicit replay-role grants. Admin-only. */
  listReplayGrants(): Promise<{
    grants: Array<{ oid: string; email: string | null; name: string | null }>;
  }> {
    return request('/api/pulsar/replay-grants');
  },

  grantReplayRole(oid: string): Promise<{ ok: true; oid: string }> {
    return request('/api/pulsar/replay-grants', {
      method: 'POST',
      body: JSON.stringify({ oid }),
    });
  },

  revokeReplayRole(oid: string): Promise<{ ok: true; oid: string }> {
    return request(`/api/pulsar/replay-grants/${encodeURIComponent(oid)}`, {
      method: 'DELETE',
    });
  },

  // ─── Workspaces (multi-tenancy) ─────────────────────────────────────
  // The active-workspace interceptor at the top of this file injects
  // ?workspace=<slug> on every other call; these endpoints are intentionally
  // workspace-agnostic on read (the user picks one from the list returned
  // here), so the interceptor's no-op-when-unset behaviour is fine.

  listWorkspaces(): Promise<{ workspaces: WorkspaceSummary[] }> {
    return request<{ workspaces: WorkspaceSummary[] }>('/api/workspaces');
  },

  getWorkspace(id: string): Promise<WorkspaceSummary> {
    return request<WorkspaceSummary>(`/api/workspaces/${encodeURIComponent(id)}`);
  },

  createWorkspace(body: CreateWorkspaceBody): Promise<WorkspaceSummary> {
    return request<WorkspaceSummary>('/api/workspaces', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateWorkspace(id: string, body: UpdateWorkspaceBody): Promise<WorkspaceSummary> {
    return request<WorkspaceSummary>(`/api/workspaces/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteWorkspace(id: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/api/workspaces/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  listWorkspaceMembers(id: string): Promise<{ members: WorkspaceMembership[] }> {
    return request<{ members: WorkspaceMembership[] }>(
      `/api/workspaces/${encodeURIComponent(id)}/members`,
    );
  },

  /** Add or change a member. Name the user by principal key, local username
   *  or SSO email. Send exactly one identifier — the union type only
   *  enforces "at least one"; the backend takes principalKey, then
   *  username, then email. */
  addWorkspaceMember(
    id: string,
    body: ({ principalKey: string } | { username: string } | { email: string }) & {
      roles: WorkspaceRole[];
    },
  ): Promise<WorkspaceMembership> {
    return request<WorkspaceMembership>(
      `/api/workspaces/${encodeURIComponent(id)}/members`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  removeWorkspaceMember(id: string, principalKey: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(
      `/api/workspaces/${encodeURIComponent(id)}/members/${encodeURIComponent(principalKey)}`,
      { method: 'DELETE' },
    );
  },

  // ── Secret backend (Phase 1D) ────────────────────────────────────────

  /** Status of the active SecretBackend — backend kind, health, secret count. */
  secretsStatus(): Promise<{
    kind: 'filesystem' | 'vault' | 'aws-ssm' | 'memory' | 'unconfigured';
    ok: boolean;
    detail?: string;
    count: number;
  }> {
    return request('/api/secrets/status');
  },

  /** Filesystem-only: rotate the master encryption key.
   *  Generates a fresh key when `newKeyHex` is omitted. Admin-only; the
   *  server records the signed-in admin in the audit log. */
  secretsRotateKey(newKeyHex?: string): Promise<{ ok: true; rotated: number }> {
    return request('/api/secrets/rotate-key', {
      method: 'POST',
      body: newKeyHex ? JSON.stringify({ newKeyHex }) : '{}',
    });
  },

  /** Vault-only: re-read VAULT_TOKEN_FILE. Admin-only. */
  secretsRefreshToken(): Promise<{ ok: true }> {
    return request('/api/secrets/refresh-token', { method: 'POST' });
  },

  // ─── Workspace branding (white-labeling — customization phase) ──────

  getWorkspaceBranding(id: string): Promise<WorkspaceBranding> {
    return request<WorkspaceBranding>(
      `/api/workspaces/${encodeURIComponent(id)}/branding`,
    );
  },

  updateWorkspaceBranding(
    id: string,
    body: WorkspaceBranding | { resetToDefaults: true },
  ): Promise<WorkspaceBranding> {
    return request<WorkspaceBranding>(
      `/api/workspaces/${encodeURIComponent(id)}/branding`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  uploadBrandingAsset(
    id: string,
    kind: 'logo' | 'favicon',
    file: File,
  ): Promise<{ url: string; filename: string; size: number }> {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    return request<{ url: string; filename: string; size: number }>(
      `/api/workspaces/${encodeURIComponent(id)}/branding/asset`,
      { method: 'POST', body: fd, headers: {} as Record<string, string> },
    );
  },

  issueCustomDomainToken(id: string): Promise<{
    customDomain: string;
    verifyToken: string;
    recordHost: string;
    recordValue: string;
    status: string;
  }> {
    return request(
      `/api/workspaces/${encodeURIComponent(id)}/branding/custom-domain/issue-token`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  verifyCustomDomain(id: string): Promise<{
    ok: boolean;
    status: 'unverified' | 'pending' | 'verified';
    error?: string;
  }> {
    return request(
      `/api/workspaces/${encodeURIComponent(id)}/branding/custom-domain/verify`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  renderEmailPreview(
    id: string,
    template: 'ssoInvite' | 'alertNotification' | 'memberApproved',
    sample?: Record<string, string>,
  ): Promise<{ html: string; markdown: string }> {
    return request(
      `/api/workspaces/${encodeURIComponent(id)}/branding/email-preview`,
      { method: 'POST', body: JSON.stringify({ template, sample: sample ?? {} }) },
    );
  },

  // ─── Workspace feature flags (customization US-007) ─────────────────

  getWorkspaceFeatures(id: string): Promise<import('./types').WorkspaceFeatureFlags> {
    return request<import('./types').WorkspaceFeatureFlags>(
      `/api/workspaces/${encodeURIComponent(id)}/features`,
    );
  },

  updateWorkspaceFeatures(
    id: string,
    body: import('./types').WorkspaceFeatureFlags,
  ): Promise<import('./types').WorkspaceFeatureFlags> {
    return request<import('./types').WorkspaceFeatureFlags>(
      `/api/workspaces/${encodeURIComponent(id)}/features`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  // ── Onboarding wizard ───────────────────────────────────────────────

  onboardingState(): Promise<OnboardingState> {
    return request<OnboardingState>('/api/onboarding/state');
  },

  onboardingSkip(): Promise<{ ok: boolean; onboarded: boolean }> {
    return request<{ ok: boolean; onboarded: boolean }>('/api/onboarding/skip', {
      method: 'POST',
    });
  },

  onboardingComplete(): Promise<{ ok: boolean; onboarded: boolean }> {
    return request<{ ok: boolean; onboarded: boolean }>('/api/onboarding/complete', {
      method: 'POST',
    });
  },

  onboardingReset(): Promise<{ ok: boolean; onboarded: boolean }> {
    return request<{ ok: boolean; onboarded: boolean }>('/api/onboarding/reset', {
      method: 'POST',
    });
  },

  // ── GitOps (gitops-flows) ────────────────────────────────────────────

  getGitopsConfig(workspaceId: string): Promise<{ config: GitopsConfig }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/config`);
  },

  putGitopsConfig(workspaceId: string, body: Partial<GitopsConfig>): Promise<{ config: GitopsConfig }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/config`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  /** Generate a fresh ed25519 deploy key. Returns the public half — paste
   *  it into the git host's deploy-key field. */
  gitopsKeygen(workspaceId: string): Promise<{ publicKey: string }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/keygen`, {
      method: 'POST',
    });
  },

  gitopsSetApiToken(workspaceId: string, token: string): Promise<{ ok: true }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/api-token`, {
      method: 'PUT',
      body: JSON.stringify({ token }),
    });
  },

  gitopsSetWebhookSecret(workspaceId: string, secret: string): Promise<{ ok: true }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/webhook-secret`, {
      method: 'PUT',
      body: JSON.stringify({ secret }),
    });
  },

  gitopsPull(workspaceId: string): Promise<{
    ok: boolean;
    outcomes: Array<{ filePath: string; flowId?: string; result: string; message?: string }>;
    conflicts: number;
    error?: string;
  }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/pull`, {
      method: 'POST',
    });
  },

  gitopsPush(
    workspaceId: string,
    body: { message: string; files?: string[]; prMode?: boolean },
  ): Promise<{
    ok: boolean;
    outcomes: Array<{ filePath: string; flowId?: string; result: string; message?: string }>;
    commitSha?: string;
    prUrl?: string;
    error?: string;
  }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/push`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  gitopsConflicts(workspaceId: string): Promise<{ conflicts: GitopsConflictRow[] }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/conflicts`);
  },

  gitopsResolveConflict(
    workspaceId: string,
    conflictId: string,
    pick: 'local' | 'remote',
  ): Promise<{ ok: boolean; resolved: boolean }> {
    return request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/conflicts/${encodeURIComponent(conflictId)}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify({ pick }),
      },
    );
  },

  /** Per-flow gitops state — keyed by flowId (US-007). */
  gitopsState(
    workspaceId: string,
  ): Promise<{
    rows: Array<{
      flowId: string;
      state: 'unmanaged' | 'in-sync' | 'drift' | 'conflict';
      conflictId?: string;
    }>;
  }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/gitops/state`);
  },

  // ─── Audit log (US-006 db-migration — cursor-paginated) ─────────────
  auditLog(query: AuditLogQuery = {}): Promise<AuditLogPage> {
    const params = new URLSearchParams();
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.actor) params.set('actor', query.actor);
    if (query.action) params.set('action', query.action);
    if (query.target) params.set('target', query.target);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    const qs = params.toString();
    return request<AuditLogPage>(`/api/audit-log${qs ? `?${qs}` : ''}`);
  },

  auditLogActors(): Promise<{ actors: string[] }> {
    return request<{ actors: string[] }>('/api/audit-log/actors');
  },

  // ─── Cutover parameters ─────────────────────────────────────────────

  /** Get the cutover view for a flow: declared parameters, per-runtime
   *  values, undeclared placeholders found in the YAML, and orphaned
   *  parameter keys that no longer appear in the YAML. */
  cutover(flowId: string): Promise<CutoverView> {
    return request<CutoverView>(`/api/flows/${encodeURIComponent(flowId)}/cutover`);
  },

  /** Declare a new parameter for a flow. Returns 409 if the key already
   *  exists. */
  declareParameter(
    flowId: string,
    body: { key: string; type: string; description?: string },
  ): Promise<void> {
    return request<void>(`/api/flows/${encodeURIComponent(flowId)}/cutover/parameters`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Update metadata (type, description, approved flag) on an existing
   *  declared parameter. Does not touch runtime values. */
  patchParameter(
    flowId: string,
    key: string,
    body: { type?: string; description?: string; approved?: boolean },
  ): Promise<void> {
    return request<void>(
      `/api/flows/${encodeURIComponent(flowId)}/cutover/parameters/${encodeURIComponent(key)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  },

  /** Remove a declared parameter and all its runtime values. */
  deleteCutoverParameter(flowId: string, key: string): Promise<void> {
    return request<void>(
      `/api/flows/${encodeURIComponent(flowId)}/cutover/parameters/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    );
  },

  /** Set (or replace) the full value map for one runtime. The `values`
   *  object is a `{ paramKey: value }` map — all keys are replaced
   *  atomically. */
  setRuntimeValues(
    flowId: string,
    runtimeId: string,
    values: Record<string, string>,
  ): Promise<void> {
    return request<void>(
      `/api/flows/${encodeURIComponent(flowId)}/cutover/values/${encodeURIComponent(runtimeId)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ values }),
      },
    );
  },

  /** Promote (copy) runtime values from one environment to another.
   *  Returns a diff of changed keys so the SPA can show a summary. */
  promoteCutover(
    flowId: string,
    body: { from: string; to: string; overrides?: Record<string, string> },
  ): Promise<{ ok: boolean; diff: Array<{ key: string; from?: string; to: string }> }> {
    return request(
      `/api/flows/${encodeURIComponent(flowId)}/cutover/promote`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  /** Audit history for all cutover operations on this flow. */
  cutoverHistory(
    flowId: string,
    limit = 50,
  ): Promise<{ entries: CutoverHistoryEntry[] }> {
    return request(`/api/flows/${encodeURIComponent(flowId)}/cutover/history?limit=${limit}`);
  },

  /** Reveal the plaintext value of a secret parameter for a given runtime.
   *  An optional `reason` string is logged to the audit trail. */
  revealSecret(
    flowId: string,
    key: string,
    runtimeId: string,
    reason?: string,
  ): Promise<string> {
    return request<{ value: string }>(
      `/api/flows/${encodeURIComponent(flowId)}/cutover/secrets/${encodeURIComponent(key)}/${encodeURIComponent(runtimeId)}/reveal`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      },
    ).then((r) => r.value);
  },

  // ─── JMS queues (embedded ActiveMQ broker on the runtime) ───────────
  // The runtime ships an in-process ActiveMQ Classic broker with KahaDB
  // persistence; these endpoints surface its state — equivalent to SAP
  // CPI's "Message Queues" monitor. `runtimeId` is optional and only
  // honoured when the multi-runtime registry is configured.

  listQueues(runtimeId?: string): Promise<QueueListResponse> {
    const q = runtimeId ? `?runtimeId=${encodeURIComponent(runtimeId)}` : '';
    return request<QueueListResponse>(`/api/queues${q}`);
  },

  browseQueue(
    name: string,
    opts: { limit?: number; runtimeId?: string } = {},
  ): Promise<QueueBrowseResponse> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.runtimeId) params.set('runtimeId', opts.runtimeId);
    const qs = params.toString();
    return request<QueueBrowseResponse>(
      `/api/queues/${encodeURIComponent(name)}/browse${qs ? '?' + qs : ''}`,
    );
  },

  purgeQueue(name: string, runtimeId?: string): Promise<QueuePurgeResponse> {
    const q = runtimeId ? `?runtimeId=${encodeURIComponent(runtimeId)}` : '';
    return request<QueuePurgeResponse>(
      `/api/queues/${encodeURIComponent(name)}/purge${q}`,
      { method: 'POST' },
    );
  },

  // ─── Runtime-scoped queue helpers (US-001 / US-002) ─────────────────
  // Hit the new `/api/runtimes/:id/queues/*` surface. The purge call now
  // takes a `confirmation` string that the server validates against the
  // queue name BEFORE proxying to the broker (typed-name confirmation).
  // The legacy helpers above keep the old `/api/queues` shape for
  // back-compat with the existing flat panel.

  listRuntimeQueues(runtimeId: string): Promise<QueueListResponse> {
    return request<QueueListResponse>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/queues`,
    );
  },

  browseRuntimeQueue(
    runtimeId: string,
    name: string,
    limit?: number,
  ): Promise<QueueBrowseResponse> {
    const qs = limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return request<QueueBrowseResponse>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/queues/${encodeURIComponent(name)}/browse${qs}`,
    );
  },

  purgeRuntimeQueue(
    runtimeId: string,
    name: string,
    confirmation: string,
  ): Promise<QueuePurgeResponse> {
    return request<QueuePurgeResponse>(
      `/api/runtimes/${encodeURIComponent(runtimeId)}/queues/${encodeURIComponent(name)}/purge`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirmation }),
      },
    );
  },

  // ─── Sub-flows ─────────────────────────────────────────────────────────

  /** List all flows marked as sub-flows, with their resolved callId, inputs,
   *  and optional output contract. Used by the Process Call picker and the
   *  `validateProcessCall` helper in `subflowContract.ts`. */
  subflows(): Promise<import('./next/subflowContract').SubFlowSummary[]> {
    return request('/api/subflows');
  },
};
